import { useState, useMemo } from 'react';
import {
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
  TagType,
  TaskTagDto,
} from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { FormCombobox, ComboboxTagOption, SelectedTagsHeader } from '@sib/ui';
import { EditingTag, useSearch } from '@sib/utils';
import { TaskField } from '@sib/utils/tasks';
import {
  TaskTagFieldFooter,
  TaskTagFieldValue,
  BulkEditTagsDialog,
  TagDialog,
} from './components';
import { useTagSelection } from './use-tag-selection';
import {
  useTaskFieldOptions,
  convertTaskTagsToOptions,
} from './TaskTagField.utils';

interface TagFieldProps {
  organizationUuid?: string | null;
  initialTags?: TaskTagDto[];
  readOnly?: boolean;
  callbacks: {
    onTagCreateSuccess: () => void;
    onTagDeleteSuccess: () => void;
    onTagCreateError: () => void;
    onTagDeleteError: () => void;
  };
}

export interface ContactCategoryDto {
  id: string;
  name: string;
  type: 'category';
}

export const TaskTagField = ({
  organizationUuid,
  initialTags = [],
  readOnly = false,
  callbacks,
}: TagFieldProps) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<EditingTag | null>(null);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const { onSearch, debouncedSearchTerm, searchTerm } = useSearch();

  const {
    toggleTag,
    removeTag,
    clearAllTags,
    allSelectedValues,
    selectedTagsCount,
  } = useTagSelection();

  const initialTagOptions = useMemo(
    () =>
      convertTaskTagsToOptions(initialTags).filter(opt =>
        opt?.label
          ?.toString()
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase())
      ),
    [initialTags, debouncedSearchTerm]
  );

  const defaultTagIds = useMemo(
    () => initialTagOptions.map(opt => opt.value),
    [initialTagOptions]
  );

  const { options, customTagOptions, customTagAutocompleteProps } =
    useTaskFieldOptions({
      organizationUuid,
      debouncedSearchTerm,
      defaultTagIds,
    });

  const { mutate: createTag, isPending: isCreating } = useCreateTag({
    onSuccess: () => {
      setDialogOpen(false);
      callbacks.onTagCreateSuccess();
    },
    onError: () => callbacks.onTagCreateError(),
  });

  const { mutate: updateTag, isPending: isUpdating } = useUpdateTag({
    onSuccess: () => setDialogOpen(false),
  });

  const { mutate: deleteTag, isPending: isDeleting } = useDeleteTag({
    onSuccess: (_, deletedUuid) => {
      setDialogOpen(false);
      removeTag(deletedUuid);
      callbacks.onTagDeleteSuccess();
    },
    onError: () => callbacks.onTagDeleteError(),
  });

  const handleTagSave = (name: string, color: string, uuid?: string) => {
    if (uuid) return updateTag({ uuid, name, color });

    createTag({ name, color });
  };

  const isSaving = isCreating || isUpdating;

  const handleBulkDeleteTags = (uuids: string[]) => {
    uuids.forEach(uuid => deleteTag(uuid));
    removeTag(uuids);
  };

  const handleRemoveTag = (uuid: string) => deleteTag(uuid);

  const handleOpenAddTagDialog = () => {
    setEditingTag(null);
    setDialogOpen(true);
  };

  const handleOpenBulkEditDialog = () => {
    setBulkEditDialogOpen(true);
  };

  const handleEditTag = (tag: EditingTag) => {
    setEditingTag(tag);
    setDialogOpen(true);
  };

  const handleOptionSelect = (option: { value: string; field?: string }) => {
    toggleTag(option.value, option.field);

    return true;
  };

  const selectedTagsHeader = useMemo(
    () => (
      <SelectedTagsHeader
        selectedOptions={initialTagOptions}
        selectedValues={allSelectedValues}
        onToggleTag={toggleTag}
      />
    ),
    [initialTagOptions, allSelectedValues, toggleTag]
  );

  return (
    <>
      <FormCombobox<
        string[],
        {
          color?: string;
          isPredefined?: boolean;
        }
      >
        name={`${TaskField.Tag}.${TagType.Custom}`}
        label="Tag"
        isMultiSelect
        HeaderWrapperProps={{ className: 'px-0 py-0 border-0' }}
        Header={selectedTagsHeader}
        PopoverContentProps={{
          className:
            'min-w-[var(--radix-popover-trigger-width)] max-h-[400px] overflow-auto',
        }}
        OptionComponent={props => (
          <ComboboxTagOption
            {...(!readOnly &&
              !props.isPredefined &&
              !props.isCollapsible && { onEdit: handleEditTag })}
            {...props}
          />
        )}
        {...(!readOnly && {
          Footer: (
            <TaskTagFieldFooter
              onTagAdd={handleOpenAddTagDialog}
              onTagEdit={handleOpenBulkEditDialog}
            />
          ),
        })}
        options={options}
        value={allSelectedValues}
        {...(!readOnly && { onOptionSelect: handleOptionSelect })}
        {...customTagAutocompleteProps}
        onInputValueChange={onSearch}
        searchValue={searchTerm}
        searchPlaceholder={t(
          'components.taskForm.fields.tag.searchPlaceholder'
        )}
        showEmpty
        readOnly={readOnly}
        renderValue={() => (
          <TaskTagFieldValue
            selectedTagsCount={selectedTagsCount}
            {...(!readOnly && { onClearAll: clearAllTags })}
          />
        )}
      />
      {!readOnly && (
        <>
          <TagDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            tagToEdit={editingTag}
            onSave={handleTagSave}
            isSaving={isSaving || isDeleting}
            onRemove={handleRemoveTag}
          />
          <BulkEditTagsDialog
            open={bulkEditDialogOpen}
            onOpenChange={setBulkEditDialogOpen}
            tags={customTagOptions?.map(tag => ({
              uuid: tag.value,
              name: tag.label as string,
              color: tag.color,
            }))}
            onBulkDelete={handleBulkDeleteTags}
            isDeleting={isDeleting}
          />
        </>
      )}
    </>
  );
};
